if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        this.addEventListener('change', (event) => {
          const target = this.getInputForEventTarget(event.target);
          this.updateSelectedSwatchValue(event);

          if (this.dataset.nextylQuoteProduct === 'true') {
            this.updateNextylQuoteVariant(event);
            return;
          }

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
            },
          });
        });
      }

      getNextylOptionGroups() {
        return Array.from(this.querySelectorAll(':scope > .product-form__input')).filter(
          (group) => group.querySelector('input[type="radio"], select')
        );
      }

      getNextylSelectedValues(groups) {
        return groups.map((group) => {
          const selectedInput = group.querySelector('input:checked, select');
          return selectedInput ? selectedInput.value : null;
        });
      }

      resolveNextylQuoteVariant(event, variants, groups, selectedValues) {
        const exact = variants.find((item) =>
          item.options.length === selectedValues.length &&
          item.options.every((value, index) => value === selectedValues[index])
        );
        if (exact) return exact;

        const changedGroup = event.target.closest('.product-form__input');
        const changedIndex = groups.indexOf(changedGroup);
        if (changedIndex < 0) return null;

        const changedValue = event.target.tagName === 'SELECT'
          ? event.target.value
          : event.target.value;
        const candidates = variants.filter((item) => item.options[changedIndex] === changedValue);
        if (!candidates.length) return null;

        return candidates
          .map((item, order) => ({
            item,
            order,
            score: item.options.reduce(
              (score, value, index) =>
                score + (index !== changedIndex && value === selectedValues[index] ? 1 : 0),
              0
            ),
          }))
          .sort((a, b) => b.score - a.score || a.order - b.order)[0].item;
      }

      applyNextylResolvedOptions(groups, variant) {
        groups.forEach((group, index) => {
          const value = variant.options[index];
          const select = group.querySelector('select');
          if (select) {
            select.value = value;
            Array.from(select.options).forEach((option) => {
              option.toggleAttribute('selected', option.value === value);
            });
          } else {
            group.querySelectorAll('input[type="radio"]').forEach((input) => {
              input.checked = input.value === value;
            });
          }

          const selectedValue = group.querySelector('[data-selected-value], [data-selected-swatch-value]');
          if (selectedValue) selectedValue.textContent = value;
        });
      }

      updateNextylQuoteVariant(event) {
        const groups = this.getNextylOptionGroups();
        const selectedValues = this.getNextylSelectedValues(groups);
        const variantsNode = this.querySelector('[data-nextyl-product-variants]');
        if (!variantsNode) return;

        let variants;
        try {
          variants = JSON.parse(variantsNode.textContent);
        } catch (_) {
          return;
        }

        const variant = this.resolveNextylQuoteVariant(event, variants, groups, selectedValues);
        if (!variant) return;

        this.applyNextylResolvedOptions(groups, variant);

        const selectedVariantNode = this.querySelector('[data-selected-variant]');
        if (selectedVariantNode) selectedVariantNode.textContent = JSON.stringify(variant);

        const productInfo = this.closest('product-info');
        if (productInfo) {
          productInfo.currentVariant = variant;

          const sku = productInfo.querySelector('[id^="Sku-"]');
          const skuValue = sku?.querySelector('span:last-child');
          if (skuValue) skuValue.textContent = variant.sku || '';

          if (typeof productInfo.updateURL === 'function') {
            productInfo.updateURL(productInfo.dataset.url, variant.id);
          } else if (this.dataset.updateUrl !== 'false') {
            window.history.replaceState({}, '', productInfo.dataset.url + '?variant=' + variant.id);
          }

          if (typeof productInfo.updateShareUrl === 'function') {
            productInfo.updateShareUrl(variant.id);
          }
          if (typeof productInfo.updateVariantInputs === 'function') {
            productInfo.updateVariantInputs(variant.id);
          }
          if (typeof productInfo.updateMedia === 'function') {
            productInfo.updateMedia(variant);
          }
        }

        document.dispatchEvent(new CustomEvent('variant:changed', {
          detail: { variant }
        }));
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          const previouslySelected = Array.from(target.options)
            .find((option) => option.getAttribute('selected'));
          previouslySelected?.removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value], [data-selected-swatch-value]');
          if (selectedSwatchValue) selectedSwatchValue.textContent = value;
        }
      }
    }
  );
}
